/**
 * GalleryLightbox — the full-screen preview for a gallery photograph.
 *
 * Adapted from the lecture image lightbox that shipped in 19dddb1
 * (`src/components/lectures/ImageLightbox.tsx`). The behaviour that matters is
 * preserved verbatim, because it is the reason the component exists:
 *
 *   - focus is TRAPPED inside the dialog while it is open, and RESTORED to
 *     the thumbnail that opened it when it closes;
 *   - ESC closes it, the arrow keys move between photographs;
 *   - the page behind it cannot be scrolled while it is open;
 *   - `aria-modal` / `aria-labelledby` / `aria-describedby` are wired up.
 *
 * WHAT CHANGED IN THE ADAPTATION
 * ------------------------------
 * The lecture build resolved each image to `{ url, width, height, alt }`
 * server-side. A gallery photo is now identified by its media uuid ALONE
 * (`GalleryPhotoRef.media_uuid`), and everything else — the large-rung URL,
 * the native size, the alt text — comes from `cmsMediaIndex`. The uuid list is
 * filtered by `GalleryPhotos` before it reaches this component, so every entry
 * here is known to the index; the lookup is still defensive because the
 * generated index can lag the CMS by a build.
 *
 * WHY THE LARGE RUNG AND NOT `cmsMediaUrl(uuid, 'large')` ALONE
 * ------------------------------------------------------------
 * `cmsMediaUrl` falls back to the ORIGINAL when a rung is missing, and an
 * original can be several megabytes — the lightbox is exactly the wrong place
 * to download one. So the large rung is used when it exists, and the largest
 * available WebP rung (never the original) when it does not.
 *
 * WHY RADIX AND NOT THE SHARED `ui/Dialog`
 * ----------------------------------------
 * The shared dialog is a small centred panel with a title/footer/actions. A
 * lightbox is a full-bleed dark stage with one image and no panel; the shared
 * primitive hard-codes layout classes that would have to be fought with
 * Tailwind utility ordering. Using `@radix-ui/react-dialog` directly keeps the
 * behaviour (focus trap/restore, ESC, scroll lock) and lets the stage be a
 * stage.
 *
 * WHY IT IS CLIENT-ONLY (PRERENDER SAFETY — DO NOT REMOVE)
 * ---------------------------------------------------------
 * Every public page is PRERENDERED with `renderToString`. Radix's `Portal`
 * renders nothing on the server (it mounts into `document.body` from an
 * effect), so the whole component stays unmounted until the client has taken
 * over. This `mounted` flag plus the direct Radix usage are what keep
 * `npm run build` green; removing either breaks the prerender.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as RadixDialog from '@radix-ui/react-dialog'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { cmsMediaIndex } from '@/content/cms/media.generated'
import { cmsMediaLargestWebpUrl } from '@/utils/cmsMediaUrl'
import { mediaAltForLocale } from '@/cms/mediaAlt'

export interface GalleryLightboxProps {
  /** Every photo in the gallery, as `{ uuid, caption? }` in display order. */
  photos: Array<{ uuid: string; caption?: string }>
  /**
   * The index of the photo being previewed, or `null` when the lightbox is
   * closed. The parent owns this so "which thumbnail is open" is one piece of
   * state shared with the grid.
   */
  index: number | null
  /** Called when the reader navigates to another photograph. */
  onIndexChange: (index: number) => void
  /** Called when the lightbox should close. */
  onClose: () => void
  /**
   * The thumbnail that opened the lightbox.
   *
   * Radix restores focus to its own `Dialog.Trigger` on close, and this
   * component has none: the trigger is a button that lives in the gallery (a
   * sibling, not a child). Without this the reader's focus falls back to
   * `<body>` after closing, i.e. to the top of the document. The caller hands
   * over the clicked element so it can be focused again.
   */
  trigger?: HTMLElement | null
}

/**
 * The full-screen URL for a photo.
 *
 * The `large` rung (1600px nominal) is the sharpest rung that is still not an
 * original; when a source is narrower than that, the largest generated WebP
 * rung stands in. Never the original — see the header.
 */
function largePhotoUrl(uuid: string): string | undefined {
  const entry = cmsMediaIndex[uuid]
  if (!entry) return undefined
  return entry.urls.large ?? cmsMediaLargestWebpUrl(uuid)
}

/** A chevron, drawn inline — see the lecture implementation for why not
 *  `@iconify/react`: it renders an empty <span> during SSR. */
function Chevron({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-6 h-6"
    >
      {direction === 'left' ? <path d="m15 18-6-6 6-6" /> : <path d="m9 18 6-6-6-6" />}
    </svg>
  )
}

/** A cross, drawn inline — same reasoning as the chevron. */
function Cross() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  )
}

export default function GalleryLightbox({
  photos,
  index,
  onIndexChange,
  onClose,
  trigger,
}: GalleryLightboxProps) {
  const { t, i18n } = useTranslation()
  const reduced = useReducedMotion()

  /**
   * Browser-only affordance: Radix's `Portal` needs a `document` to mount
   * into. Keep the whole component unmounted until the client takes over.
   */
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  const open = index !== null
  const total = photos.length
  const active = index ?? 0
  const uuid = photos[active]?.uuid
  const entry = uuid ? cmsMediaIndex[uuid] : undefined
  const src = uuid ? largePhotoUrl(uuid) : undefined

  const goPrev = useCallback(() => {
    if (total === 0) return
    onIndexChange((active - 1 + total) % total)
  }, [active, total, onIndexChange])

  const goNext = useCallback(() => {
    if (total === 0) return
    onIndexChange((active + 1) % total)
  }, [active, total, onIndexChange])

  /** Radix handles ESC; the arrow keys are the one gesture a keyboard reader
   *  expects from an image viewer and Radix knows nothing about galleries. */
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        goPrev()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        goNext()
      }
    },
    [goPrev, goNext],
  )

  if (!mounted || !entry || !src) return null

  const duration = reduced ? 0 : 0.16
  // The media library owns alt text; an empty string is a valid (decorative)
  // alt and is not replaced with a generic label.
  const alt = mediaAltForLocale(entry, i18n.language)

  return (
    <RadixDialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <AnimatePresence>
        {open && (
          <RadixDialog.Portal forceMount>
            <RadixDialog.Overlay asChild forceMount>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: reduced ? 0 : 0.15, ease: 'easeOut' }}
                className="fixed inset-0 z-[60] bg-ink/95 backdrop-blur-sm"
              />
            </RadixDialog.Overlay>

            <RadixDialog.Content
              asChild
              forceMount
              onKeyDown={onKeyDown}
              onCloseAutoFocus={(event) => {
                // Radix restores focus to its own `Dialog.Trigger`, which does
                // not exist here. Focus the thumbnail ourselves instead.
                event.preventDefault()
                trigger?.focus()
              }}
            >
              <motion.div
                initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97 }}
                transition={{ duration, ease: [0.4, 0, 0.2, 1] }}
                className="fixed inset-0 z-[61] flex flex-col focus:outline-none"
              >
                {/* Screen-reader-only accessible name + description. The
                    visible counter and caption below carry the same
                    information for everybody else. */}
                <RadixDialog.Title className="sr-only">
                  {t('gallery.lightbox.title')}
                </RadixDialog.Title>
                <RadixDialog.Description className="sr-only">
                  {t('gallery.lightbox.counter', { current: active + 1, total })}
                </RadixDialog.Description>

                <button
                  type="button"
                  onClick={onClose}
                  aria-label={t('gallery.lightbox.close')}
                  data-lightbox-close
                  className="absolute right-3 top-3 z-20 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
                >
                  <Cross />
                </button>

                {/* The stage. Clicking the empty area around the photograph
                    closes it; a click on the image itself does not, because
                    `event.target !== event.currentTarget` there. */}
                <div
                  data-lightbox-stage
                  className="relative flex-1 flex items-center justify-center px-14 py-4 sm:px-24 sm:py-8 min-h-0"
                  onPointerDown={(event) => {
                    if (event.target === event.currentTarget) onClose()
                  }}
                >
                  {total > 1 && (
                    <button
                      type="button"
                      onClick={goPrev}
                      aria-label={t('gallery.lightbox.prev')}
                      data-lightbox-prev
                      className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
                    >
                      <Chevron direction="left" />
                    </button>
                  )}

                  {/* Not lazy: it is the only thing on screen and the reader
                      asked for it. The width/height come from the media index
                      so the browser reserves the box before it decodes. */}
                  <img
                    src={src}
                    alt={alt}
                    width={entry.width ?? undefined}
                    height={entry.height ?? undefined}
                    data-lightbox-image
                    className="max-h-full max-w-full object-contain"
                  />

                  {total > 1 && (
                    <button
                      type="button"
                      onClick={goNext}
                      aria-label={t('gallery.lightbox.next')}
                      data-lightbox-next
                      className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
                    >
                      <Chevron direction="right" />
                    </button>
                  )}
                </div>

                {/* The caption and the counter. */}
                <div className="shrink-0 px-4 pb-5 sm:px-8 sm:pb-6 text-center">
                  {/* A gallery-specific caption wins over the media-library alt
                      text; fall back to the alt so a caption-less photo still
                      names what it shows. */}
                  {(photos[active]?.caption ?? alt) && (
                    <p className="text-sm text-white/85 max-w-3xl mx-auto">
                      {photos[active]?.caption ?? alt}
                    </p>
                  )}
                  <span
                    className="mt-3 inline-block text-sm text-white/60 tabular-nums"
                    data-lightbox-counter
                  >
                    {t('gallery.lightbox.counter', { current: active + 1, total })}
                  </span>
                </div>
              </motion.div>
            </RadixDialog.Content>
          </RadixDialog.Portal>
        )}
      </AnimatePresence>
    </RadixDialog.Root>
  )
}
